import { gql } from '@apollo/client';

import { EventRequest, Quote, Event, User } from '../../types';

type EventRequestFields = Pick<
  EventRequest,
  | 'id'
  | 'uuid'
  | 'completed'
  | 'close_date'
  | 'description'
  | 'budget'
  | 'budget_flexible'
  | 'additional_info'
  | 'item_quantity'
  | 'tags'
  | 'category_tier2'
  | 'categories_tier2'
  | 'event_request_tags'
  | 'preferred_suppliers'
  | 'potential_suppliers'
  | 'close_source'
  | 'isHighValueLead'
  | 'close_stage'
>;

export type QuoteFields = Pick<
  Quote,
  | 'uuid'
  | 'state'
  | 'read_timestamp'
  | 'published'
  | 'created'
  | 'is_excl_vat_price'
  | 'isShortlisted'
  | 'introMessage'
  | 'quotePriceDetails'
  | 'vatStatus'
>;

export type EventFields = Pick<
  Event,
  'uuid' | 'id' | 'date' | 'postcode' | 'custom_event_name' | 'type'
>;

export type UserFields = Pick<User, 'firstName' | 'id' | 'business_user'>;

export type EventRequestResult = EventRequestFields & {
  quotes: QuoteFields[];
  events: EventFields & {
    users: UserFields & {
      user_preferences?: { showCustomerPhoneNumber?: boolean };
    };
  };
  quote_limited?: boolean;
  isMatchedByMatchingService?: boolean;
};

export interface RequestById {
  result: EventRequestResult;
}

export const REQUEST_BY_ID = gql`
  query customerEventRequest($filter: CustomerEventRequestFilter!) {
    result: customerEventRequest(filter: $filter) {
      uuid
      id
      completed
      close_date
      close_source
      close_stage
      category_tier2
      categories_tier2 {
        sys_name
        name
        parent
      }
      description
      budget
      budget_flexible
      preferred_suppliers
      potential_suppliers
      isMatchedByMatchingService
      isHighValueLead
      minPrice
      maxPrice
      robex: event_request_supplier_notified(
        take: 12
        where: { method: { notIn: ["preferred_supplier"] } }
      ) {
        suppliers {
          uuid
          name
          slug
          tagline
          average_rating
          images
        }
      }
      additional_info {
        courses
        children
        waiters
        water
        crockery
        power
        bar_type
        supplier_setup
      }
      item_quantity
      tags
      categories_tier2 {
        parent
        name
        sys_name
      }
      quotes {
        uuid
        state
        read_timestamp
        covers
        service_fee
        supplier_id
        supportText
        created
        published
        is_excl_vat_price
        isShortlisted
        priceBreakdown {
          excludingVatPrices {
            inPennies {
              priceExclCommission
              priceInclCommission
              priceInclCommissionAndServiceFee
              priceInclCommissionAndServiceFeeAndProtection
              commissionPrice
              serviceFeePrice
              protectionPrice
            }
            formattedToCurrency {
              priceExclCommission
              priceInclCommission
              priceInclCommissionAndServiceFee
              priceInclCommissionAndServiceFeeAndProtection
              commissionPrice
              serviceFeePrice
              protectionPrice
            }
          }
          includingVatPrices {
            inPennies {
              priceExclCommission
              priceInclCommission
              priceInclCommissionAndServiceFee
              priceInclCommissionAndServiceFeeAndProtection
              commissionPrice
              serviceFeePrice
              protectionPrice
            }
            formattedToCurrency {
              priceExclCommission
              priceInclCommission
              priceInclCommissionAndServiceFee
              priceInclCommissionAndServiceFeeAndProtection
              commissionPrice
              serviceFeePrice
              protectionPrice
            }
          }
        }
      }
      quote_limited
      event_request_tags {
        tags_event_request_tags_tag_uuidTotags {
          name
          uuid
          root_tags {
            key
          }
        }
      }
      events {
        uuid
        id
        date
        postcode
        custom_event_name
        type
        users {
          id
          firstName
          business_user
          user_preferences {
            showCustomerPhoneNumber
          }
        }
      }
    }
  }
`;
